import React from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import ProductModel from "@src/models/product"
import { useTranslation } from "react-i18next"

const badgeSx = {
  borderRadius: "4px",
  px: "8px",
  py: "4px",
  minHeight: "24px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
}

const badgeTextSx = {
  fontSize: "10px",
  lineHeight: 1.2,
  color: "#FFF",
  fontWeight: 700,
  whiteSpace: "nowrap",
  letterSpacing: "0.02em"
}

const BadgeProductType = ({
  detail,
  onSaleColor
}: {
  detail: ProductModel
  onSaleColor: string | null
}) => {
  const { t } = useTranslation()
  const {
    out_of_stock,
    promotion_price,
    discount_percent,
    is_best_seller,
    is_new,
    // 2026-10-12 presale: gallery badge shows "Sap giam X%" when a
    // scheduled flash sale is the only promo attached to the product.
    flash_sales: flashSales
  } = detail || {}

  const upcomingFlashSale = (flashSales || []).find(
    s => s && s.status === "upcoming" && s.from && s.to
  )

  if (out_of_stock) {
    return (
      <Box
        sx={{
          ...badgeSx,
          backgroundColor: "#F46647"
        }}
      >
        <Typography sx={badgeTextSx}>{t("out_of_stock")}</Typography>
      </Box>
    )
  }

  // 2026-10-12 presale: scheduled sale (no live promotion yet). Render
  // the sale-red "Sap giam X%" gallery badge so the customer can see
  // the upcoming deal from the thumbnail.
  const detailOrigPrice = Number(detail?.original_price || detail?.price || 0)
  const isCurrentlyOnSale =
    (typeof promotion_price === "number" &&
      promotion_price > 0 &&
      promotion_price < (detailOrigPrice || Infinity)) ||
    (typeof detail?.sale_price === "number" &&
      detail.sale_price > 0 &&
      detail.sale_price < (detailOrigPrice || Infinity))

  if (upcomingFlashSale && !isCurrentlyOnSale) {
    return (
      <Box
        sx={{
          ...badgeSx,
          backgroundColor: onSaleColor || "#D8042C"
        }}
      >
        <Typography sx={badgeTextSx}>
          {t("presale_gallery_badge", {
            defaultValue: "Sắp giảm {{percent}}%",
            percent: Number(upcomingFlashSale.discount_percent || 0)
          })}
        </Typography>
      </Box>
    )
  }

  if (isCurrentlyOnSale) {
    const calcPercent =
      Number(discount_percent) > 0
        ? Number(discount_percent)
        : detailOrigPrice > 0 && detail?.sale_price
          ? Math.round(((detailOrigPrice - detail.sale_price) / detailOrigPrice) * 100)
          : 0

    return (
      <Box
        sx={{
          ...badgeSx,
          backgroundColor: onSaleColor || "#D8042C",
          minWidth: "52px"
        }}
      >
        <Typography sx={{ ...badgeTextSx, fontSize: "16px" }}>
          {calcPercent > 0
            ? `${calcPercent.toFixed(0)}%`
            : t("badge_on_sale", { defaultValue: "SALE" })}
        </Typography>
      </Box>
    )
  }

  if (is_new) {
    return (
      <Box sx={{ ...badgeSx, backgroundColor: "#85A1FC" }}>
        <Typography sx={badgeTextSx}>{t("badge_new")}</Typography>
      </Box>
    )
  }

  if (is_best_seller) {
    return (
      <Box sx={{ ...badgeSx, backgroundColor: "#8DC960" }}>
        <Typography sx={badgeTextSx}>{t("best_seller")}</Typography>
      </Box>
    )
  }

  return null
}

export default BadgeProductType
